import { useState } from "react";
import CloseButton from "../../components/Application/CloseButton";
import useCurrentUser from "../../hooks/useCurrentUser";
import { ArrowRightIcon } from "../../Icons";

const DEFAULT_LOGO = "/login-logo.png";

const tableOfContents = [
  "A. General Orientation to Agency",
  "B. Organizational Chart",
  "C. Human Resources Processes",
  "D. Care and Services provided by the Agency",
  "E. Organization Safety Review",
  "F. Infection Prevention and Control / Influenza Program",
  "G. Performance improvement process",
  "H. Confidentiality of Agency and Patient Information/HIPAA",
  "I. Type of Care Delivered in Patient's Environment",
  "J. Available Community Resources",
  "K. Equipment Management",
  "L. Home Safety Issues",
  "M. Storage, Handling and Access to Supplies, Medical Gases and Drugs",
  "N. Identification, Handling and Disposal of Hazardous or Infectious Materials",
  "O. Infection Control Practices",
  "P. Actions in Unsafe Situations",
  "Q. Other Patient Care and Service Responsibilities",
  "R. Specific Tests to be Performed by Agency Personnel",
  "S. Advance Directives",
  "T. Appropriate Policies and Procedures",
  "U. Screening for Abuse and Neglect",
  "V. Guideline for Appropriate Referrals, including Timeliness",
  "W. Emergency Preparedness within the Agency and Home Care Setting",
  "X. Information Regarding Services by Other Members",
  "Y. Death and Dying",
  "Z. Patient Rights and Responsibilities",
  "AA. Ethical Issues",
  "BB. Cultural Diversity and Sensitivity",
  "CC. Corporate Compliance Program",
];

const handbookPages = [
  {
    pageNo: 1,
    title: "Table of Contents",
    sections: [
      {
        heading: "Contents",
        type: "list",
        items: tableOfContents,
      },
    ],
  },
  {
    pageNo: 2,
    title: "A-D Overview",
    sections: [
      {
        heading: "A. General Orientation to Agency (4-001)",
        paragraphs: [
          "Mission",
          "INFINITE HOME HEALTH, INC is guided by a tradition of personal, clinical, and technological excellence.",
          "We are dedicated to providing the highest quality home-based patient care with compassion and respect for each person.",
          "Values",
          "INFINITE HOME HEALTH, INC recognizes these values and their role in fulfilling our mission:",
          "Committed to Our Patients",
          "We recognize the unique physical, emotional, and spiritual needs of each person receiving health care in the home. We strive to extend the highest level of courtesy, safety and service to patients, family/caregivers, visitors, and each other.",
          "Committed to Leadership",
          "We deliver state-of-the-art home health services with identified centers of excellence. We engage in a wide range of continuing education, clinical education, and other programs for professionals and the public.",
          "Committed to Excellence",
          "We strive to create an environment of teamwork and participation, where, through continuous performance improvement and open communication, health care professionals pursue excellence and take pride in their work, the organization, and their personal development. We believe that the quality of our human resources-organization personnel, physicians, and volunteers-is the key to our continued success. We provide physicians an environment that fosters high quality diagnosis and treatment. We maintain financial viability through a cost-effective operation to meet our long-term commitment to the community.",
          "Committed to a Culture of Safety and Quality",
          "We strive to create a culture of safety and quality by developing a code of conduct, providing education, encouraging open communication, encouraging leaders to provide a team approach to safety and quality initiatives, providing leadership that defines how patients, family/caregivers, visitors, and other members of the community can help identify and manage issues of safety and quality, and implementing changes identified by the annual organization evaluation in order to maintain the culture of safety and quality.",
        ],
      },
      {
        heading: "B. Organizational Chart",
        paragraphs: ['Refer to "Attachment I" titled Organizational Chart & Contact Info.'],
      },
      {
        heading: "C. Human Resources Processes",
        paragraphs: ['Refer to Agency\'s "Human Resources Policies and Procedures".'],
      },
      {
        heading: "D. Care and Services provided by the Agency (2-001)",
        paragraphs: [
          "Home health services are provided on an intermittent basis to patients in their place of residence.",
        ],
        type: "list",
        subheading: "Scope of Services",
        items: ["SN", "PT", "OT", "ST", "MSW", "CHHA"],
      },
    ],
  },
  {
    pageNo: 3,
    title: "E-F Policy and Definitions (1-9)",
    sections: [
      {
        heading: "E. Organization Safety Review",
        paragraphs: ['Refer to "Emergency Management" policies and procedures.'],
      },
      {
        heading: "F. Infection Prevention and Control / Influenza Program (9-001.2)",
        paragraphs: [
          "POLICY",
          "INFINITE HOME HEALTH, INC is committed to reducing the risk of acquisition and transmission of health care associated infections (HAIs). Recognized prevention and control mechanisms will be implemented for planning, surveillance, identification, prevention/controls, and reporting procedures. To determine the effectiveness of the infection control plan, INFINITE HOME HEALTH, INC will measure, assess, improve, and redesign (as appropriate) the surveillance, identification, prevention, and control function annually through its performance improvement program. [Refer to Occupational Safety and Health Administration (OSHA) website for most recent standards on occupational exposure (www.osha.gov).]",
          "Definitions",
          "The following definitions describe terms used by INFINITE HOME HEALTH, INC throughout this section.",
        ],
        type: "list",
        items: [
          "Aseptic: Near-sterile technique referring to methods used to prevent the spread of microorganisms.",
          "Blood: Human blood, human blood components, and products made from human blood.",
          "Bloodborne Pathogens: Pathogenic microorganisms that are present in human blood and can cause disease in humans. These pathogens include, but are not limited to, Hepatitis B virus (HBV) and human immunodeficiency virus (HIV).",
          "Body Fluids: Emesis, sputum, feces, urine, semen, vaginal secretions, cerebrospinal fluid (CSF), synovial fluid, pleural fluid, pericardial fluid, amniotic fluid, and human breast milk; along with other fluids, such as nasal secretions, saliva, sweat, and tears.",
          "Clinical Laboratory: Workplace where diagnostic or other screening procedures are performed on blood or other potentially infectious materials.",
          "Contaminated: The presence, or the reasonably anticipated presence, of blood or other potentially infectious materials on an item or surface.",
          "Contaminated Materials: Reusable materials that have been exposed to or contaminated by blood or body fluids. These materials may be transported to destinations outside the patient's home (i.e., blood specimens to laboratories).",
          "Contaminated Laundry: Laundry that has been soiled with blood or other potentially infectious materials, or may contain sharps.",
          "Contaminated Sharps: Any object capable of cutting or penetrating the skin that has been in contact with blood or body fluids, including, but not limited to, needles, scalpels, broken glass, broken capillary tubes, and exposed ends of dental wires.",
        ],
      },
    ],
  },
  {
    pageNo: 4,
    title: "Definitions (10-21)",
    sections: [
      {
        heading: "Definitions Continued",
        type: "list",
        items: [
          "Contaminated Wastes: Disposable materials that have been exposed to or contaminated by blood or body fluids.",
          "Decontamination: The use of physical or chemical means to remove, inactivate, or destroy bloodborne pathogens on a surface or item to the point where they are no longer capable of transmitting infectious particles and the surface or item is rendered safe for handling, use, or disposal.",
          "Engineering Controls: Controls that isolate or remove the bloodborne pathogens hazard from the workplace (e.g., sharps disposal containers, self-sheathing needles, needleless IV systems).",
          "Exposure Incident: A specific eye, mouth, or other mucous membrane, non-intact skin, or parenteral contact with blood or other potentially infectious material(s) that results from the performance of organization personnel's duties.",
          "Hand Antisepsis: Refers to either antiseptic hand wash or antiseptic hand rub.",
          "Hand Hygiene: A general term that applies to either hand washing, antiseptic hand wash, antiseptic hand rub, or surgical hand antisepsis.",
          "Hand Washing: Washing hands with plain (i.e., non-antimicrobial) soap and water.",
          "Infectious Wastes are defined as: A. Sharps; B. Blood, Blood Products, and Body Fluids; C. Microbiological Waste; D. Contaminated Lab Waste.",
          "Licensed Health Care Professional: A person whose legally permitted scope of practice allows him/her to independently perform the activities required by the Hepatitis B vaccination and post-exposure evaluation and follow-up.",
          "HBV: Hepatitis B Virus.",
          "HIV: Human Immunodeficiency Virus.",
          "Occupational Exposure: Reasonably anticipated skin, eye, mucous membrane, or parenteral contact with blood or other potentially infectious material(s) that may result from the performance of organization personnel's duties.",
        ],
      },
    ],
  },
  {
    pageNo: 5,
    title: "Definitions (22-32)",
    sections: [
      {
        heading: "Definitions Continued",
        type: "list",
        items: [
          "Other Potentially Infectious Materials: Any body fluid that potentially contains blood, e.g., feces, nasal secretions, sputum, sweat, tears, urine, emesis, human breast milk, saliva, and all body fluids in situations where it is difficult or impossible to differentiate between body fluids; A. Any unfixed tissue or organ, other than intact skin, from a human, living or dead; B. HIV-containing cell or tissue cultures, organ cultures; and HIV- or HBV-containing culture medium or other solutions; and blood, organs, or other tissues from experimental animals infected with HIV or HBV.",
          "Penetrating Injury: Piercing mucous membranes or the skin barrier through such events as needle sticks, human bites, cuts, and abrasions.",
          "Personal Protective Equipment (PPE): Specialized clothing or equipment worn by personnel for protection against a hazard. PPE will be considered appropriate only if it does not permit blood or other potentially infectious materials to pass through or reach organization personnel's work clothes, street clothes, undergarments, skin, eyes, mouth or other mucous membranes under normal conditions of use and for the duration of time for which the protective equipment will be used. General work clothes (e.g., uniforms, pants, shirts, or blouses) not intended to function as protection against a hazard, are not considered to be personal protective equipment.",
          "PPD: Purified protein derivative for intradermal tuberculosis skin tests (Mantoux).",
          "Problem-Oriented or Outbreak Response Surveillance: Surveillance that is conducted to measure the occurrence of specific infections in multiple patients at the same time.",
          "Regulated Waste: Liquid or semi-liquid blood or other potentially infectious materials in a liquid or semi-liquid state if compressed; items that are caked with dried blood or other potentially infectious materials and are capable of releasing these materials during handling; contaminated sharps; and pathological and microbiological wastes containing blood or other potentially infectious materials.",
          "Source Individual: Any individual, living or dead, whose blood or other potentially infectious materials may be a source of occupational exposure to personnel.",
          "Sterilize: The use of a physical or chemical procedure to destroy all microbial life, including highly resistant bacterial endospores.",
          "Standard Precautions: An approach to infection control where all human blood and certain human body fluids are treated as if known to be infectious for HIV, HBV, and other bloodborne pathogens.",
          "Targeted or Priority-Directed Surveillance: Surveillance activities that focus on specific patient populations or specific procedures.",
          "Waterless Antiseptic Agent: An antiseptic agent that does not require use of exogenous water. After applying such an agent, the hands are rubbed together until the agent has dried.",
        ],
      },
    ],
  },
  {
    pageNo: 6,
    title: "Standard Precautions (9-007.1)",
    sections: [
      {
        heading: "Definitions and Procedure",
        type: "list",
        items: [
          "Work Practice Controls: Controls that reduce the likelihood of exposure by altering the manner in which a task is performed (e.g., prohibiting recapping of needles by a two (2)-handed technique).",
          "Nosocomial Infections: This type of infection is known as a hospital-acquired infection. These infections are a result of treatment in a hospital. Infections are considered nosocomial if they first appear 48 hours or more after hospital admission or within 30 days after discharge.",
        ],
        paragraphs: [
          "STANDARD PRECAUTIONS (9-007.1)",
          "PURPOSE: To reduce the risk of exposure to and transmission of infections when caring for patients.",
          "POLICY: Organization personnel will adhere to the following precautions and will instruct patients and family/caregivers in infection control precautions, as appropriate to the patient's care needs.",
          "Note: Patients may be given a copy of this procedure, if needed as a teaching tool.",
          "Note: INFINITE HOME HEALTH, INC has the right to limit the practice of organization personnel, if patient safety is in question.",
          "Definition: Under standard precautions, blood and certain body fluids of all patients are considered potentially infectious for bloodborne pathogens, such as human immunodeficiency virus (HIV), and Hepatitis B virus (HBV). Standard precautions apply to blood and other body fluids potentially containing blood or bloodborne pathogens. These body fluids include: emesis, sputum, feces, urine, semen, vaginal secretions, cerebrospinal fluid (CSF), synovial fluid, pleural fluid, pericardial fluid, and amniotic fluid. Standard precautions should be used with other body fluids, such as nasal secretions, saliva, sweat, and tears when they contain visible blood or other potentially infectious materials and it is impossible to differentiate between body fluids.",
          "PROCEDURE",
          "General Precautions"
        ],
        subheading: "Hand Hygiene",
        subItems: [
          "Hand hygiene will be performed to prevent cross-contamination between the patient and personnel. (See 'Hand Hygiene' Policy No. 9-009.)",
          "When hands are visibly dirty, contaminated with proteinaceous material, or are visibly soiled with blood or other body fluids, wash hands with either a non-antimicrobial or antimicrobial soap and water.",
          "When hands are not visibly soiled, use an alcohol-based hand rub for routinely decontaminating hands.",
          "An alternative to use of an alcohol based hand rub is to wash hands with an antimicrobial soap and water.",
        ],
      },
    ],
  },
  {
    pageNo: 7,
    title: "PPE - Gloves",
    sections: [
      {
        heading: "Personal Protective Equipment (Policy No. 9-008)",
        paragraphs: ["Gloves"],
        subheading: "Glove Use",
        subItems: [
          "The use of gloves (intact latex or vinyl of appropriate size and quality) is important when personnel have cuts, abraded skin, chapped hands, dermatitis, etc.",
          "Gloves are to be worn when there is actual or potential contact with blood or other potentially infectious materials.",
          "Contact with non-intact or abraded skin is anticipated.",
          "Touching contaminated items or surfaces.",
          "Performing invasive procedures.",
          "Handling any drainage appliance.",
          "Taking a rectal temperature.",
          "Shaving a patient with a safety razor.",
          "Obtaining laboratory specimens.",
          "Patients have active bleeding.",
          "Cleaning of body fluids and decontamination procedures.",
          "Performing wound care.",
          "Entering the room of, or providing care for, patients who are colonized or infected with vancomycin-resistant enterococci (VRE) or multidrug-resistant Staphylococcus aureus (MRSA).",
          "Handling soiled linen.",
          "Sterile gloves are to be worn for sterile procedures.",
          "Gloves are to be changed between tasks and procedures on the same patient; after changing or cleaning an incontinent patient; after removing an old dressing; and when the integrity of the glove is in doubt.",
          "Gloves should never be washed or disinfected for reuse.",
        ],
      },
    ],
  },
  {
    pageNo: 8,
    title: "PPE - Gowns, Masks, Sharps, Specimens",
    sections: [
      {
        heading: "Personal Protective Equipment Continued",
        type: "list",
        items: [
          "General purpose utility gloves (e.g., rubber household gloves) will be used for housekeeping chores involving potential blood contact and for instrument cleaning and decontamination procedures. Utility gloves may be decontaminated and reused, but should be discarded if they are peeling, cracked, or discolored, or if they have punctures, tears, or other evidence of deterioration.",
          "Gloves are not necessarily needed for general care or during casual contact, such as bathing of intact skin or assisting with ambulation.",
          "Gloves are to be worn by the family/caregiver when direct contact with any body substance is anticipated (blood, urine, pus, feces, saliva, drainage of any kind.)",
          "Gowns: The use of gowns is required when splashes to the skin and/or clothing are likely or when caring for patients with epidemiologically important microorganisms, such as multi-drug resistant organisms.",
          "The gowns will be made of or lined with fluid-proof or fluid-resistant material and will protect all areas of exposed skin. The type and characteristics will depend on the task and degree of exposure anticipated.",
          "Mask/Protective Eyewear: Masks, protective eyewear, or face shields are required when contamination of mucosal membranes, eyes, mouth, or nose is possible, such as splashes or aerosolization of material.",
          "They are not required for routine care.",
          "A NIOSH-certified N95 respiratory mask must be worn when caring for patients with suspected or confirmed Mycobacterium Tuberculosis.",
          "Resuscitation Equipment: One-way valve pocket masks, resuscitation bags, or other ventilation devices will be provided to personnel where the need for emergency mouth-to-mouth resuscitation would be required.",
          "Sharps: After use, needles and other sharps will be placed directly into a puncture-proof container located in the immediate patient care area. Needles must not be recapped, bent, broken, or clipped.",
          "Whenever possible, needleless protective devices will be utilized in the provision of patient care. Appropriate staff will be involved in the selection of these products.",
          "Laboratory Specimens: Laboratory specimens will be placed in a Ziploc plastic bag or other leak-proof container.",
          "The leak-proof container will be transported to the office or alternate lab site in a puncture-resistant container that is properly labeled.",
        ],
      },
    ],
  },
  {
    pageNo: 9,
    title: "Labels and Housekeeping & Hygiene",
    sections: [
      {
        heading: "Specimen / Labels / Housekeeping",
        type: "list",
        items: [
          "Specimens transported to the office will be placed in a designated storage container located in the dirty supply area.",
          "Biohazard labels will be used to prevent accidental injury or illness to personnel exposed to hazardous or potentially hazardous conditions that are out of the ordinary, unexpected, or not readily apparent.",
          "Labels will state-BIOHAZARD-or the hazard symbol, readable at the minimum distance of five (5) feet.",
          "Personnel will be informed as to the meaning of the labels.",
          "Labels will be affixed as close as possible to respective hazards.",
          "Labels will be used to identify equipment, containers, refrigerators, and rooms containing hazardous agents.",
          "If labels are not used, other effective means will be used, such as RED bagging.",
          "Housekeeping procedures at INFINITE HOME HEALTH, INC's location will be implemented to ensure that the worksite is maintained in a clean and sanitary condition.",
          "The organization will determine and implement an appropriate written schedule for cleaning and decontamination based upon location, type of surface, and tasks/procedures performed.",
          "Appropriate disinfectant (e.g., household bleach 5.25% mixed 1:10 with water) should be used to clean floors, toilet bowl, tub, shower, sink, countertops, and soiled furniture. This solution will be discarded after each use, or at least every 24 hours.",
          "Sponge and mops used to clean up body fluid spills should not be rinsed out in the kitchen sink or used where food is prepared.",
          "Dirty mop water should be poured down the toilet, rather than in the sink.",
          "Rooms should be kept well aired to decrease the risk of colds, flu, and other airborne communicable disease.",
          "Infectious organisms may be found in animal wastes, birdcages, cat litter boxes, and fish tanks. They should be maintained by someone other than a person with HIV disease or other causes of immunosuppression.",
        ],
      },
    ],
  },
  {
    pageNo: 10,
    title: "Blood/Body Fluid Spills and Waste Disposal",
    sections: [
      {
        heading: "Spills, Hygiene, and Waste",
        type: "list",
        items: [
          "Humidifiers and air conditioners can harbor infectious organisms, and they should be cleaned and serviced regularly.",
          "All bins, pails, cans (e.g., waste cans) intended for reuse, which have a reasonable likelihood for becoming contaminated with blood and other potentially infectious materials, will be inspected and decontaminated weekly. They will also be cleaned and decontaminated immediately, or as soon as feasible, upon visible contamination.",
          "Blood/body fluid spills should be mopped or wiped up using disposable towels or wipes with hot soapy water, then disinfected with bleach as described in policy. If cleanup is done by hand, disposable gloves must be worn.",
          "Disposable towels or wipes used in the cleanup should be bagged to prevent leaking and secondary exposure. A heavy-duty plastic bag should be used for bagging this type of waste with double bagging. The bag should be disposed in accordance with local and state regulations.",
          "Personal items, such as toothbrushes, razors, and enema equipment, should never be shared.",
          "Maintaining a state of personal cleanliness is the key to reducing infection transmission from person to person. This includes bathing regularly, washing hands after use of bathroom facilities, after contact with one's own body fluids, and before preparing food.",
          "Waste Disposal references: Hazardous Waste Handling Policy No. 6-010, and Contaminated Waste Disposal Policy No. 9-013.",
          "General Waste: Materials not contaminated or visibly soiled with blood or other infectious waste, such as diapers, incontinence pads, non-soiled PPE, dressing wrappers, or IV tubing not used for blood administration.",
          "General waste should be disposed of in a securely fastened plastic bag and can be placed into the patient's trash receptacle.",
          "Regulated Medical Waste may include liquid or semi-liquid blood or other potentially infectious material; contaminated items that would release blood or other potentially infectious materials in a liquid or semi-liquid state if compressed.",
        ],
      },
    ],
  },
  {
    pageNo: 11,
    title: "Laundry, Medical Waste, and Syringe/Sharps",
    sections: [
      {
        heading: "Regulated Medical Waste and Syringe/Sharps Disposal",
        type: "list",
        items: [
          "Items caked with blood or other potentially infectious materials and capable of releasing these materials during handling are regulated medical waste.",
          "Pathological and microbiological wastes containing blood or other potentially infectious material are regulated medical waste.",
          "Place regulated medical waste into a leak-proof, heavy duty, securely fastened plastic bag. Items should be double bagged when contamination risk exists or first bag may tear; dispose according to local/state regulation.",
          "In the home setting, a sharps disposal container will be available for clinician/patient/family caregiver use. Uncapped needle is placed directly in container. At 3/4 full, container is sealed and transported to dirty supply area in office. A new container should be provided for future use.",
          "If patient is self-administering medications and generating one or more syringes per day, patient should notify garbage collection service or local Department of Public Health for local/state disposal requirements.",
        ],
      },
      {
        heading: "Laundry",
        type: "list",
        items: [
          "Handling and changing of linens: contaminated laundry should be handled with minimal agitation.",
          "Towels and washcloths should not be shared by different users.",
          "Gloves and other appropriate personal protective equipment are to be worn when handling soiled linen.",
          "Soiled clothing and linens should be soaked as promptly as possible. Ideally machine wash hot (160°F) soapy water; if appropriate, add bleach for colorfast material. For low temperature cycles, suitable low-temperature chemicals at proper concentration should be used.",
          "When contaminated laundry is wet and likely to soak through/leak from bag to container, transport in containers or bags that prevent leakage to exterior.",
          "Laundry and linens should be carried away from the body.",
          "Equipment/Nondisposable Instruments - Bedpans/Urinals/Commodes heading continues on next section.",
        ],
      },
    ],
  },
  {
    pageNo: 12,
    title: "Equipment/Nondisposable Instruments and Kitchen",
    sections: [
      {
        heading: "Equipment/Nondisposable Instruments",
        type: "list",
        items: [
          "Bedpans and urinals should be used by only one (1) patient and should be cleaned on a regular basis with household detergent.",
          "Shared commodes do not require special precautions unless blood, contaminated body substance, or fluid is present. If soiled, the commode should be cleaned with a 1:10 dilution of bleach.",
          "Thermometers are not supplied by the organization, but may be owned by patients.",
          "Electronic thermometers with disposable sheaths need no special precautions unless visibly soiled; when soiled, they should be wiped with a disinfectant solution.",
          "Glass thermometers used in the home should be rinsed with soap and water before and after use. If used by more than one family/caregiver member, soak in 70-90% ethyl alcohol for 30 minutes followed by rinse under stream of water between users.",
          "Any nondisposable equipment returned to organization stock will be placed in dirty supply area and thoroughly wiped down with organization-approved disinfectant. After proper cleaning, the equipment may be returned to stock.",
          "If a nondisposable piece of equipment (e.g., stethoscope, blood pressure apparatus, bandage scissors) comes in contact with blood/body fluids, a 1:10 dilution of bleach or other approved disinfectant is used to clean it. Soiled blood pressure cuffs should be washed in hot, soapy water.",
          "Dressing supplies contaminated with patient's blood/body fluids should be double bagged in plastic bags, tied securely, and labeled contaminated then placed with household trash for garbage pickup according to local and state regulations.",
        ],
      },
      {
        heading: "Kitchen/Food Preparation",
        type: "list",
        items: [
          "Hand washing: Proper hand washing techniques should be observed prior to touching food.",
          "General hygiene: Tasting of food during cooking should be done with a new, clean spoon each time. Wash spoon with soap and water immediately after tasting.",
          "Cleaning of kitchen: Counters, sinks, and floors in the kitchen should be free from food particles and cleaned with a disinfectant regularly.",
          "Refrigerator: The interior should be cleaned with soap and warm water regularly to control molds.",
          "Food freshness: Observe expiration dates and general freshness of food. Do not use cracked eggs due to likelihood of Salmonella contamination.",
        ],
      },
    ],
  },
  {
    pageNo: 13,
    title: "Kitchen, Special Considerations and G. Policy",
    sections: [
      {
        heading: "Kitchen/Food Preparation Continued",
        type: "list",
        items: [
          "Food storage: Store open packages of food (e.g., sugar) in covered containers to discourage infestation.",
          "Food preparation: Pork, poultry, and eggs should be thoroughly cooked before eating. Porous (e.g., wood) cutting boards used for poultry should not also be used for fruits and vegetables.",
          "Dishes/utensils: Wash dishes and utensils in hot, soapy water. The water should be hot enough to require the use of lined gloves. Allow dishes to air dry. Known infected persons do not need separate dishes or utensils if they are washed as described.",
          "Sponges: Sponges used to clean in the kitchen should not be the same sponges used to clean bathrooms and body fluid spills. Sponges used to clean bathrooms and body fluid spills should be disinfected with bleach and replaced periodically.",
        ],
      },
      {
        heading: "Special Considerations for a Person with HIV Disease",
        type: "list",
        items: [
          "Unpasteurized milk, raw eggs, or products containing raw eggs or cracked or non-intact eggs should be avoided. They have been associated with Salmonella infections and may be problematic, especially for the person with HIV disease or other immune-suppressed diseases.",
          "All fresh produce should be washed thoroughly.",
        ],
      },
      {
        heading: "Other Considerations",
        type: "list",
        items: [
          "Eating, drinking, smoking, applying makeup or lip balm, or handling contact lenses should be avoided in work areas where there is a reasonable chance of exposure.",
          "Sterile technique will be employed for sterile dressing changes, IV insertion, IV site care, phlebotomy, tracheal suctioning, insertion of a urinary catheter, and whenever appropriate to prevent infection.",
          "Disinfectants: HIV is inactivated rapidly after being exposed to chemical germicides. HIV can be inactivated after exposure for ten (10) minutes to any of the following: A. Chlorine bleach (1:10 dilution), B. Alcohol (70-95%), C. Quaternary Ammonium (TRI-GUAT), D. Phenolic (Vesphene II).",
        ],
      },
      {
        heading: "G. Performance Improvement process (4-015)",
        paragraphs: [
          "POLICY",
          "Senior management will carry the responsibility to guide the organization's efforts in improving organizational performance in governance, management, clinical and support activities; to define expectations of the performance improvement activities; and to generate the plan and processes the organization will utilize to assess, improve and maintain quality of care and service.",
        ],
      },
    ],
  },
  {
    pageNo: 14,
    title: "G. Performance Improvement Procedure ",
    sections: [
      {
        heading: "All personnel participation and procedure",
        paragraphs: [
          "All personnel will be active participants in the organization's performance improvement activities.",
          "PROCEDURE",
        ],
        type: "list",
        items: [
          "Senior management will participate in educational activities to increase their level of understanding and ability to implement performance improvement activities. The educational activities may include seminars, consultations, readings, periodicals, benchmarking, and review of available information from other organizations regarding the occurrence of sentinel events to reduce the risk of similar sentinel events within the organization.",
          "Set expectations for performance improvement and manage processes to improve organization performance. (See example Self-Assessment of Health Care Organizational Performance Addendum 4-015.A.)",
          "Adopt a scientific, problem-solving approach to performance improvement. The scientific, problem-solving approach will include, minimally: planning for performance improvement with integration of information from other relevant activities (risk management, utilization management, quality control, infection control surveillance, patient safety program); setting priorities for improvement and adjusting priorities in response to unusual or urgent events; systematic assessment of performance through comparison of organizational performance internally over time, to similar organizations, and to external sources; implementing improvement based on assessment and comparison data; maintaining achieved improvements.",
        ],
      },
    ],
  },
  {
    pageNo: 15,
    title: "Performance Improvement Procedure",
    sections: [
      {
        heading: "Design, resources, and statistical methods",
        type: "list",
        items: [
          "Identifying and establishing activities to measure patient outcomes.",
          "Ensure that new or modified services or processes are designed well and incorporate: needs and expectations of patients, staff, and others; information about potential risks to patients, when available; current knowledge, when available and relevant; testing and analysis to determine whether design or redesign is an improvement.",
          "Ensure that the organization adheres to the Joint Commission's published guidelines for describing information in its Quality Report.",
          "Allocate resources for assessing and improving the organization's performance by: assigning organization personnel to participate in performance improvement activities; providing adequate time; creating and maintaining information systems and data management processes to support collecting, managing and analyzing data; utilizing appropriate statistical techniques to analyze and display data.",
          "Statistical facts to include: run charts that display summary comparison data, scatter diagrams, control charts that display variations and trends over time, histograms, Pareto charts, cause-and-effect or fishbone diagrams, process flowcharts.",
          "Provide organization personnel training in effective approaches and methods of assessment and improvement.",
        ],
      },
    ],
  },
  {
    pageNo: 16,
    title: "Performance Improvement Procedure",
    sections: [
      {
        heading: "Patient safety program and infection control integration",
        type: "list",
        items: [
          "Assessing the adequacy of human, information, physical, and financial resources allocated to support performance improvement and patient safety.",
          "Analyze and assess the effectiveness of their contributions to improving organization performance, including review of leadership performance against pre-established, objective process criteria. The senior management, also, measure and assess the performance improvement and safety improvement activities.",
          "Senior management will ensure that an integrated patient safety program is implemented throughout the organization by assigning qualified individual(s) or an interdisciplinary group to manage the program, defining oversight scope, establishing procedures for immediate response and internal/external reporting of failures, defining responses to unanticipated adverse events and proactive risk reduction, and annual governing body reports on failures and improvement actions.",
          "Senior management will ensure that the infection control program is an integral part of the organization's safety and performance improvement program by participating in design and implementation of annual influenza vaccination program; tracking vaccination rates; evaluating reasons for declining vaccination; providing vaccination data annually; participating in educational activities; establishing ongoing risk assessment for acquisition/transmission of infectious agents; setting goals for hand hygiene compliance; and establishing at least annual review of the infection control program.",
          "All other organization personnel will follow assigned performance and safety improvement responsibilities.",
        ],
      },
    ],
  },
  {
    pageNo: 17,
    title: "Confidentiality/HIPAA and Patient Privacy Rights",
    sections: [
      {
        heading: "Confidentiality continuation",
        type: "list",
        items: [
          "Copies of clinical records, or excerpts of same, cannot be removed from organization except by subpoena, where statutory law requires it, or on written authorization of the organization. This confidential information will only be mailed in an envelope designated confidential.",
          "Patients will be allowed access to their protected health information. (See Patient Requests for Access to PHI Policy No. 5-020.)",
          "The organization respects the safety and security of patients and their property.",
          "All clinical records will be kept in a locked cabinet/room when not being utilized. The Clinical Supervisor or designee will be responsible for the key. No unauthorized individual will be allowed access to clinical records.",
          "The following patient information will be secured after business hours:",
          "A. Clinical records",
          "B. Field clinical records",
          "C. Patient intake information",
          "D. Minutes of patient care meetings",
          "E. Performance improvement data",
          "F. Clinical notes prior to filing in clinical record",
          "G. Signed physician (or other authorized licensed independent practitioner) orders",
          " Information contained in performance improvement reports will not contain individual patient or personnel information.",
          "INFINITE HOME HEALTH, INC will apply appropriate sanctions against any organization personnel who fail to comply with its privacy policies and procedures.",
        ],
      },
      {
        heading: "PATIENT PRIVACY RIGHTS",
        paragraphs: ["POLICY"],
        type: "list",
        items: [
          "The organization will respect and safeguard all protected health information about the patients it serves.",
          "Each patient will be provided with information about his/her privacy rights at the time of admission to INFINITE HOME HEALTH, INC.",
          "To assist with fully understanding patient privacy rights and responsibilities, all policies will be available to organization personnel, patients and their representatives, as well as to other organizations and the interested public.",
        ],
      },
    ],
  },
  {
    pageNo: 18,
    title: "Patient Privacy Rights",
    sections: [
      {
        heading: "PROCEDURE",
        type: "list",
        items: [
          "The patient will be provided with information about his/her privacy rights in the organization's Notice of Privacy Practices, which will be given to the patient during the admission visit. The patient's privacy rights include:",
          "A. A right to adequate notice of the uses and disclosures of protected health information that may be made by INFINITE HOME HEALTH, INC. (See Notice of Privacy Practices Addendum 1-016.A.)",
          "B. A right to request privacy protection for protected health information. (See Patient Requests for Privacy Restrictions Policy No. 1-018, and Patient Requests for Confidential Communications Policy No. 1-019.)",
          "C. A right of access to inspect and retain a copy of his/her protected health information. (See Patient Requests for Access to PHI Policy No. 5-020.)",
          "D. A right to request that the organization amend protected health information or a record about the individual in a designated record set for as long as the protected health information is maintained in the designated record set. (See Patient Requests to Amend PHI Policy No. 5-021.)",
          "E. A right to receive an accounting of disclosures of protected health information made by INFINITE HOME HEALTH, INC in the six (6) years prior to the date on which the accounting is requested. (See Patient Requests for Accounting of PHI Disclosures Policy No. 5-022.)",
          "INFINITE HOME HEALTH, INC will make a good faith effort to obtain the patient's written acknowledgement of receipt of this notice. A separate signature/initials line for this acknowledgement may be located on the consent form. If an acknowledgement cannot be obtained, the admitting clinician will document his/her efforts to obtain the acknowledgement and the reason why it was not obtained in the clinical note.",
          "The notice will be promptly revised and distributed whenever there is a material change to the uses or disclosures, the individual's rights, organization's legal duties, or other privacy practices stated in the notice. A material change to any term of the notice will not be implemented prior to the effective date of the revised notice, unless required by law.",
          "INFINITE HOME HEALTH, INC will prominently post the notice and make the notice available through its website.",
          "The patient's legal representative may exercise the patient's rights when a patient is incompetent or a minor.",
          "When a patient has questions about his/her privacy rights, requests additional information, or would like to exercise one of these rights, he/she will be referred to the appropriate individual or office designated by INFINITE HOME HEALTH, INC on the Notice of Privacy Practices.",
        ],
      },
    ],
  },
  {
    pageNo: 19,
    title: "Continuation and H. Confidentiality/HIPAA Start",
    sections: [
      {
        heading: "Performance improvement staff responsibilities",
        type: "list",
        items: [
          "Be involved in performance improvement activities.",
          "Promote communication and coordination of performance improvement activities, as well as contribute to those activities.",
          "Forward relevant information regarding performance improvement activities to senior management and to the Performance Improvement Coordinator.",
          "Take action on recommendations generated through performance improvement activities, as outlined in the organization's written performance improvement plan and as recommended in the Joint Commission's Sentinel Event Alerts and National Patient Safety Goals, based on care and service provided by the organization.",
        ],
      },
      {
        heading: "H. Confidentiality of Agency and Patient Information/HIPAA (1-015)",
        paragraphs: ["POLICY"],
        type: "list",
        items: [
          "Organization and its personnel will maintain as confidential all patient-protected health information. Protected health information will be used and disclosed in accordance with the organization's policies and procedures. (See Uses and Disclosures of PHI Policy No. 5-015.)",
          "On the first day during the orientation process, this Confidentiality Policy will be reviewed by organization personnel.",
          "All organization personnel will be required to sign a Confidentiality Agreement at the time of hire.",
          "Organization personnel will have access to the minimum necessary protected health information of patients needed to carry out their duties.",
          "Use and disclosure of protected health information will be carried out according to accepted policies and procedures. (See Uses and Disclosures of PHI Policy No. 5-015.)",
          "Patients will not be discussed by clinical or nonclinical personnel outside of the context of professional conversation regarding those patients' conditions and care.",
          "Comments and conversations relating to patients made by physicians, nurses, or other organization personnel will be made in confidential settings and only as needed for care-team discussion.",
          "An agreement and consent for services form will be signed by the patient upon admission.",
          "Valid authorizations for use and disclosure of information will be obtained, as required. (See Authorizations for Use or Disclosure of PHI Policy No. 5-016.)",
        ],
      },
    ],
  },
  {
    pageNo: 20,
    title: "I. Type of Care and J. Community Resources",
    sections: [
      {
        heading: "I. Type of Care Delivered in Patient's Environment (4-002.1)",
        paragraphs: ["POLICY"],
        type: "list",
        items: [
          "All patients, regardless of race, color, religion, age, gender, sexual orientation, disability (mental or physical), communicable disease, or place of national origin have the right to receive the same quality of care and to have access to the home health resources they need to meet their health care needs.",
        ],
      },
      {
        heading: "I. Procedure",
        type: "list",
        items: [
          "The care and resources each patient receives, as well as the skill level and training of organization personnel, will be based on the standards of care and practices outlined within this manual, as well as on the patient's health care needs.",
          "The organization will maintain a clinical record review process to ensure that the organization's policies and procedures are followed by all clinical personnel, including direct and contract personnel.",
          "The organization will not discriminate against an individual based on whether or not the individual has executed an Advance Directive. (See Advance Directives Policy No. 1-004.)",
        ],
      },
      {
        heading: "J. Available Community Resources (2-055)",
        paragraphs: ["POLICY"],
        type: "list",
        items: [
          "Organization personnel, as well as the patient and family/caregiver, will have access to community resources in the provision of patient education appropriate to patient and family/caregiver needs.",
          "The organization will maintain a list of community resources and appropriate contacts that have patient education materials available for use in the home health setting.",
          "Resources available may include, but will not be limited to: American Diabetic Association, American Heart Association, American Lung Association, Cancer Society, Drug and pharmaceutical companies, national groups supporting diseases such as ALS and MS, and support groups such as Ostomy and Y-Me.",
          "The organization will use this information in conjunction with other materials developed by the organization.",
        ],
      },
    ],
  },
  {
    pageNo: 21,
    title: "O. Infection Control Practices and P. Unsafe Situations",
    sections: [
      {
        heading: "O. Infection Control Practices (2-052) - Continuation",
        type: "list",
        items: [
          "Standard precautions instruction includes: hand washing, protecting skin membranes, use of antiseptic cleaners, disposing of sharps in puncture-resistant containers, breaking of needles, food and drink in patient area, transmission of infections, personal protective equipment, cleaning and decontamination schedules (if appropriate), handling of soiled laundry and linen, and emergency responses.",
          "When appropriate, the patient and family/caregiver will receive verbal and written information on standard precautions.",
          "The use of standard precautions must be demonstrated by the patient and family/caregiver prior to assuming responsibility for care.",
          "Ongoing assessments should continually address the use of standard precautions.",
          "Documentation of patient and family/caregiver instruction in the clinical record will include: information taught, adaptations made to the environment, patient and family/caregiver understanding, return demonstrations in use of equipment (if appropriate), response to teaching, and additional learning needs.",
        ],
      },
      {
        heading: "P. Actions in Unsafe Situations",
        paragraphs: ['Refer to Agency\'s "Clinical Policies and Procedures".'],
      },
    ],
  },
  {
    pageNo: 22,
    title: "M. Storage, Handling and Access to Supplies, Medical Gases and Drugs (2-050)",
    sections: [
      {
        heading: "M. Policy and Definitions",
        paragraphs: ["POLICY"],
        type: "list",
        items: [
          "Patients will receive information and instruction regarding storage, handling, and access to supplies and medical gases to promote safe usage.",
          "Definitions: Medical Equipment - any assistive device or equipment used by home health personnel/patient/family/caregiver to meet patient needs (wheelchairs, walkers, canes, lifts, monitors).",
          "Home Health Supplies - disposable items used by home health personnel/patients/family caregivers (sterile dressings, syringes, catheters, tubing, gloves).",
          "Gases, Oxygen and Related Equipment - equipment used to deliver gas to patient, such as oxygen tank and tubing.",
        ],
      },
      {
        heading: "M. Procedure",
        type: "list",
        items: [
          "Upon admission, patient assessment includes any medical gases and/or sterile supplies to be used in home setting, and the home medical equipment company providing service/equipment.",
          "Selection, delivery, set-up, initial instruction, and maintenance of home medical equipment are the responsibility of the equipment company.",
          "Initial home assessment includes (not limited to): use of oxygen and oxygen-related equipment including medical gas cylinders; and areas where medical gases are stored.",
        ],
      },
    ],
  },
  {
    pageNo: 23,
    title: "M Continuation and N. Hazardous/Infectious Materials",
    sections: [
      {
        heading: "M. Procedure Continuation",
        type: "list",
        items: [
          "Initial home assessment includes potential need for and use of puncture-resistant needle containers, bags for soiled dressing/linens, and gloves/protective clothing.",
          "Assessment includes appropriate actions for both clinician and patient/family caregiver while receiving home health services.",
          "Each patient with potential for handling/disposal of hazardous materials receives information on OSHA bloodborne pathogen standards and home safety information addressing hazardous waste in home setting.",
          "Materials should be reviewed initially with patient/family caregiver to assess understanding; completion/full understanding are necessary prior to assuming care and interventions that may put them at risk.",
          "On subsequent visits, clinicians observe patient/family caregiver using learned information; failure to perform according to accepted standards results in re-instruction.",
          "Documentation in clinical record includes information taught, environment adaptations, understanding, return demonstrations, response to teaching, and additional learning needs.",
        ],
      },
      {
        heading: "N. Identification, Handling and Disposal of Hazardous or Infectious Materials (2-051)",
        paragraphs: ["POLICY"],
        type: "list",
        items: [
          "Patients will receive information and instruction regarding identification, handling, and disposal of hazardous materials and wastes, in accordance with applicable law and policy references.",
          "Upon admission, assessment includes educational needs related to identification, handling, and disposal of hazardous materials and wastes.",
        ],
      },
    ],
  },
  {
    pageNo: 24,
    title: "K. Equipment Management (2-049)",
    sections: [
      {
        heading: "K. Policy",
        type: "list",
        items: [
          "Patients receive information regarding safe and effective use of home medical equipment (HME) and supplies provided by the organization, in accordance with applicable policies (including Safe and Appropriate Use of Medical Equipment and Supplies, and Medical Equipment Malfunction).",
          "When medical equipment is supplied by another organization, organization personnel will be supportive in patient teaching role.",
        ],
      },
      {
        heading: "K. Procedure",
        type: "list",
        items: [
          "Upon admission, patient assessment documents use of any medical equipment/supplies in home. HME company providing equipment and any patient instruction materials left in home will be identified.",
          "Clinician will determine patient/family/caregiver understanding level of use of equipment/supplies ordered for care, treatment, and service through interview and observation.",
          "If patient/family caregiver have knowledge/skill deficits and clinician has sound knowledge base, clinician may provide additional instruction.",
          "If patient/family caregiver have deficits and clinician does not have sufficient knowledge base, clinician should contact HME company for further instruction.",
          "Organization will maintain generic patient teaching tools from HME company for common HME in home, including oxygen, humidifiers, oxygen concentrators, oxygen cylinders, suction machines, mechanical lifts, trapeze bars, and pressure pads/pumps.",
        ],
      },
    ],
  },
  {
    pageNo: 25,
    title: "Q, R and S. Advance Directives (1-004) - Definitions",
    sections: [
      {
        heading: "Q/R References and S. Policy",
        paragraphs: [
          'Q. Other Patient Care and Service Responsibilities - Refer to Agency\'s "Administrative Policies and Procedures".',
          'R. Specific Tests to be Performed by Agency Personnel (i.e. Venipuncture, HGM) - Refer to Agency\'s "Clinical Policies and Procedures".',
        ],
      },
      {
        heading: "S. Advance Directives (1-004) Policy",
        type: "list",
        items: [
          "All adult persons have a fundamental right to make decisions relating to their own medical treatment, including acceptance/refusal of medical care.",
          "Policy encourages individuals and family/caregivers to participate in care/treatment decisions.",
          "Valid Advance Directives (living wills, Durable Power of Attorney for Health Care, DNR orders) will be followed as permitted/required by law.",
          "In absence of Advance Directives, organization will provide care according to attending physician plan and will not discriminate based on whether individual has executed an Advance Directive.",
        ],
      },
      {
        heading: "Definitions",
        type: "list",
        items: [
          "Adult: A person 18 years or older, or legally capable of consenting to his/her own medical treatment.",
          "Advance Directives: Document in which a person states choices for medical treatment.",
          "Attending Physician: Physician primarily responsible for medical care of patient receiving home health services.",
          "DNR (Do Not Resuscitate): Medical order to refrain from cardiopulmonary resuscitation if patient's heart stops beating.",
          "Patient Self-determination Act: Federal statute (OBRA) requiring provision of information regarding right to formulate advance directives.",
          "Patient Representative: Person appointed to make decisions for someone else (formally or by recognized relationship).",
          "Terminal Condition: Incurable condition caused by injury/disease/illness where, regardless of life-sustaining procedures, reasonable medical judgment predicts death.",
        ],
      },
    ],
  },
  {
    pageNo: 26,
    title: "S. Advance Directives Procedure (Part 1)",
    sections: [
      {
        heading: "Procedure",
        type: "list",
        items: [
          "Upon admission, clinician provides information regarding patient's right to make health care decisions including acceptance/refusal of treatment, execution of Advance Directives, and applicable organization policies.",
          "Written information for adult patient is provided (Advance Directive Information Statement). Clinician documents in clinical record that information was provided and discussions were recorded.",
          "If patient lacks decision-making capacity, admitting clinician provides information/direct inquiry about Advance Directives to patient's representative and documents representative name/responses.",
          "If not practical to provide information at admission, information is provided as soon as feasible after admission.",
          "During admission visit, clinician asks whether patient/representative has completed Advance Directive, DPOA, living will, or DNR order.",
          "If directive completed, clinician asks for copy and places in clinical record; if not immediately available, patient is informed of responsibility to provide copy as soon as possible.",
          "Copy handling: verify photocopy is exact and mark Copy From Original; record date of request and recipient; indicate Provided By and Received By with signature/date and document request details in clinical record.",
          "If copy is unavailable, clinician discusses contents with patient/representative, documents contents in clinical record, and communicates to other home health providers.",
        ],
      },
    ],
  },
  {
    pageNo: 27,
    title: "S. Advance Directives Procedure (Part 2)",
    sections: [
      {
        heading: "Procedure Continuation",
        type: "list",
        items: [
          "When applicable, admitting clinician documents in clinical record and notifies attending physician verbally of physician's or authorized licensed independent practitioner's order if patient has executed an Advance Directive.",
          "Patient encouraged to participate in all aspects of home health care decision-making; statements by competent patient regarding acceptance/refusal are documented in clinical record.",
          "Patient informed of any limitations organization has in respecting patient's Advance Directive.",
          "All clinicians providing care will: review Advance Directive and report discrepancies between directive and current treatment plan; utilize educational materials to answer patient questions; encourage patient discussion with physician/family/caregiver/selected advocate; assist patient wanting to develop Advance Directive by obtaining forms and providing access to outside individuals as needed.",
          "Advance Directive implementation: DPOA for Health Care is effective only when patient unable to participate in own treatment decisions; attending physician and another physician or licensed psychologist must document inability to participate.",
          "Patient's designated advocate may make treatment choices based on directive, including decisions to withhold/withdraw treatment allowing patient to die, only if clearly and convincingly authorized and with acknowledgment of possible death.",
          "Executing and implementing Advance Directive is a process, not a one-time event; on ongoing basis clinical staff keeps patient/family/caregiver/representative updated and documents discussions in clinical record.",
        ],
      },
    ],
  },
  {
    pageNo: 28,
    title: "S. Continuation, T and U (2-013) Start",
    sections: [
      {
        heading: "Advance Directives Continuation",
        type: "list",
        items: [
          "Educational information about Advance Directives and INFINITE HOME HEALTH, INC's policies and procedures regarding Advance Directives will be provided to the medical, nursing and allied health professionals, as well as home health personnel and volunteers during the orientation period.",
          "In order to educate the community about Advance Directives, INFINITE HOME HEALTH, INC will participate in community forums, as appropriate, and make written materials available regarding Advance Directives.",
          "If the state and/or region has endorsed the use of POLST/MOLST, the organization will utilize the state and regional specific forms and guidelines. (For more information: www.ohsu.edu/polst.)",
        ],
      },
      {
        heading: "T. Appropriate Policies and Procedures",
        paragraphs: ['Refer to Agency\'s "Clinical Policies and Procedures".'],
      },
      {
        heading: "U. Screening for Abuse and Neglect (2-013)",
        paragraphs: ["POLICY"],
        type: "list",
        items: [
          "The organization will report all suspected cases of abuse, neglect, or exploitation, in compliance with applicable state statutes to appropriate protection organizations.",
          "Definitions",
          "1. Abuse/Domestic Violence: Any act that constitutes a violation of the prostitution, criminal, or sexual conduct statutes; the intentional and nontherapeutic infliction of pain, injury, or any persistent course of conduct intended to produce mental or emotional distress.",
          "2. Abandonment: Evidence that the patient has been dropped off by someone, or the family unit has no intention of coming back for the patient. Other high-risk situations: drug or alcohol addiction in the family, isolation of the patient, history of untreated psychiatric problems, evidence of unusual family stress, excessive dependence of the patient on the caretaker.",
          "3. Neglect: Failure of a caretaker to supply the patient with the necessary food, clothing, shelter, health care, or supervision; or the absence, or likelihood of an absence, of necessary food, clothing, shelter, health care, or supervision for a patient.",
          "4. Exploitation: Unexplained loss of Social Security or pension checks, any evidence that material goods are being taken in exchange for care, any evidence that personal belongings of the patient (house, jewelry, car) are being taken without consent or approval of the patient.",
          "5. Caregiver: An individual or facility responsible for all or some of the patient's care, voluntarily or by agreement, such as a relative, friend, or organization personnel.",
          "6. Report: Any report received by the local welfare organization, police department, county sheriff, or licensing organization; a verbal and/or written statement of abuse and/or neglect that states the following:",
        ],
      },
    ],
  },
  {
    pageNo: 29,
    title: "L. Home Safety Issues (2-045)",
    sections: [
      {
        heading: "L. Policy and Procedure",
        type: "list",
        items: [
          "Patients receive information regarding basic home safety in accordance with applicable policies, including fire response, electrical safety, environmental/mobility safety, bathroom safety, and medication safety.",
          "Upon admission, patient's home will be assessed using home safety assessment/checklist.",
          "Based on assessment results, patient and family/caregiver learning needs are identified.",
          "Assessment with written/verbal information appropriate to patient's environment used as basis for patient instruction.",
          "As required by Joint Commission National Patient Safety goals, staff encourage patient's active involvement in own care as safety strategy and communication/reporting of concerns.",
          "Education reviewed may include: fire safety (detectors, extinguishers, fire escape route, prevention, safe use/storage of oxygen/electrical blankets/heaters/flammable items); electrical safety (extension cords, overloaded circuits, outlets, light bulbs, grounding, appliances); environmental/mobility safety (fall prevention, wheelchair/walker safety, exits/passages, handrails, loose carpets, stairway safety, lighting, emergency/disaster plans); bathroom safety (nonskid mats, slippery surfaces, grab bars, water temperature).",
          "During home visits, clinicians continually assess patient/family/caregiver comprehension/compliance to home safety and re-instruct when issues are identified.",
        ],
      },
    ],
  },
  {
    pageNo: 30,
    title: "N. Hazardous Materials and O. Infection Control",
    sections: [
      {
        heading: "N. Identification, Handling and Disposal of Hazardous or Infectious Materials (2-051) - Continuation",
        type: "list",
        items: [
          "Assessment includes environment, including temperature where gases are stored.",
          "Assessment includes use of sterile supplies related to care provided by organization.",
          "Upon admission, patient/family/caregiver using supplies (sterile and non-sterile) designate an appropriate storage area in home.",
          "Patient/family/caregiver instructed on storage of medical gases in stable/ventilated/protected area, protection from heat extremes, safe filling of portable oxygen units, proper handling of sterile supplies, response to emergency situations/accidents in home, and delivery/access to supplies.",
          "Documentation of instruction includes information taught, environment adaptations, understanding, return demonstrations, response to teaching, and additional learning needs.",
        ],
      },
      {
        heading: "O. Infection Control Practices (2-052) - Introduction",
        paragraphs: ["POLICY", "PROCEDURE"],
        type: "list",
        items: [
          "Patients will receive information and instruction regarding infection control precautions and teaching about preventing infection (see Standard Precautions and Hand Hygiene policies).",
          "Upon admission, assessment includes patient and family/caregiver knowledge regarding infection control.",
          "Based on assessment and patient needs, patient/family caregiver receives information/instruction on standard precautions.",
        ],
      },
    ],
  },
  {
    pageNo: 31,
    title: "Z. Patient Rights and Responsibilities (Continuation)",
    sections: [
      {
        heading: "Patient Rights Continuation",
        type: "list",
        items: [
          "The value or purpose of any technical procedure that will be performed, including benefits, risks, and who will perform the task/procedure.",
          "The cost of services billed to insurance(s) and/or self (verbally and in writing).",
          "The right to pain assessment and management.",
          "The right to privacy, security, and respect of property and person.",
          "The right to be free from mental, physical, sexual and/or verbal abuse, neglect, or exploitation.",
          "The right to voice a complaint or concern regarding care/service and availability of other sources to receive questions/complaints and assist in resolution.",
          "Organization policy regarding confidentiality and disclosure of medical information, including patient privacy rights related to collection of OASIS data.",
          "OASIS rights include right to be informed collection purpose, confidentiality, limited lawful disclosure, sharing of OASIS/OQI/OQOM/quality reports with surveyors as appropriate, right to refuse to answer questions, and right to see/review/request changes on assessment.",
          "The right to have communication needs met.",
          "The right to choose whether or not to participate in research, investigations, or experimental studies/clinical trials.",
          "The right to have cultural, psychosocial, spiritual, and personal values, beliefs, and preferences respected.",
          "Availability of applicable toll-free home health agency hotline and other sources for questions/complaints and assistance in resolution.",
          "Patient and family/caregiver responsibilities are explained upon admission and as needed.",
        ],
      },
    ],
  },
  {
    pageNo: 32,
    title: "U. Screening for Abuse and Neglect - Procedure",
    sections: [
      {
        heading: "Procedure",
        type: "list",
        items: [
          "Report details include:",
          "A. What has happened",
          "B. To whom it happened",
          "C. When it happened",
          "D. Where it happened",
          "E. Who was responsible for the abuse or neglect",
          "7. Individuals Mandated to Report: A professional or the professional's delegate who is engaged in the care of patients, or in education, social services, law enforcement, or in any of the related occupations, who has knowledge of the abuse or neglect of a patient, or who has reasonable cause to believe that a patient is being or has been abused or neglected, or who has knowledge that a patient has sustained a physical injury that is not reasonably explained by the history of injuries provided by the caretaker or caregiver(s) of the patient. (Note that specific reporting requirements vary from state to state.)",
          "Note: Any individual who is legally mandated to report suspected abuse and/or neglect and who intentionally fails to report such suspected abuse/neglect is guilty of a misdemeanor and is liable for damages caused by such failure.",
          "An individual who willingly makes a false abuse and/or neglect report is liable for civil action for any damages suffered by individuals who were reported as suspects in such abuse and/or neglect.",
          "Occurrences may include but are not limited to:",
        ],
      },
      {
        heading: "Occurrences may include but are not limited to",
        table: {
          headers: [
            "Physical Abuse/Domestic Violence",
            "Neglect/Failure to Provide",
            "Exploitation",
            "Verbal Abuse",
            "Sexual Abuse",
          ],
          columns: [
            [
              "Neglect",
              "Malnutrition",
              "Bruise",
              "Welt",
              "Abrasion",
              "Puncture wound",
              "Laceration",
              "Burn",
              "Dislocation",
              "Fracture",
              "Internal injury",
              "Hitting",
              "Slapping",
              "Exposure to adverse weather conditions",
            ],
            [
              "Physical care",
              "Emotional care",
              "Medical care",
              "Acceptable environment",
              "Adequate supervision",
            ],
            ["Money misuse", "Property misuse", "Money theft", "Property theft"],
            ["Verbal assault", "Verbal humiliation", "Verbal threat"],
            ["Sexual abuse", "Sexual assault", "Sexual exploitation"],
          ],
        },
      },
    ],
  },
  {
    pageNo: 33,
    title: "U. Continuation - Mandated Reporting and Occurrences",
    sections: [
      {
        heading: "Definitions and Note",
        type: "list",
        items: [
          "Report details include: what happened, to whom it happened, when it happened, where it happened, and who was responsible for abuse/neglect.",
          "Individuals Mandated to Report: professional/delegate in care, education, social services, law enforcement, or related occupations with knowledge/reasonable cause of abuse/neglect or unexplained physical injury.",
          "Failure to report suspected abuse/neglect by legally mandated individual may be misdemeanor and liable for damages; knowingly false reports may create civil liability for damages suffered by wrongly reported individuals.",
        ],
      },
      {
        heading: "Occurrences may include but are not limited to",
        type: "list",
        items: [
          "Physical Abuse/Domestic Violence: neglect, malnutrition, bruise, welt, abrasion, puncture wound, laceration, burn, dislocation, fracture, internal injury, hitting, slapping, exposure to adverse weather.",
          "Neglect/Failure to Provide: physical care, emotional care, medical care, acceptable environment, adequate supervision.",
          "Exploitation: money misuse, property misuse, money theft, property theft.",
          "Verbal Abuse: verbal assault, verbal humiliation, verbal threat.",
          "Sexual Abuse: sexual abuse, sexual assault, sexual exploitation.",
        ],
      },
    ],
  },
  {
    pageNo: 34,
    title: "AA. Ethical Issues (1-008)",
    sections: [
      {
        heading: "Policy and Definition",
        type: "list",
        items: [
          "Family/guardian may exercise patient rights when patient is incompetent or a minor.",
          "Supervisory visits with clinical disciplines will be conducted to ensure rights are honored and protected according to policy.",
          "All organization personnel, clinical and non-clinical, are oriented to patient rights/responsibilities before end of orientation and annually.",
          "Policy reference: Patient Privacy Rights Policy No. 1-016.",
          "Organization provides mechanism to respond to ethical challenges confronting health care providers through management meetings, case conferences, performance improvement meetings, and Professional Advisory Committee.",
          "Ad hoc ethics forums may be established; representation may include management, physician, social worker, nurse, consumer, clergy, attorney, or risk management representative.",
          "Organization develops/maintains resources and education programs concerning ethical issues.",
          "Patient/family/caregiver or legal representative has right to participate in discussions concerning conflict/ethical issue arising from patient care.",
          "Ethical issues include: withholding/withdrawing treatment; unsafe home situations and patient safety; nonadherence/refusal of interventions; choosing to stay in neglectful/abusive environment; over/under-treatment by physician/family/caregiver; caregiver participation in decisions; informed consent; confidentiality/privacy rights; care without insurance/payment sources; issues causing ethical conflict/moral dilemma; and ethical business practices including marketing, admission, transfer, discharge, and billing.",
        ],
      },
      {
        heading: "Procedure",
        type: "list",
        items: [
          "Organization personnel may discuss ethical concerns with immediate supervisors.",
          "Further discussions may occur in management meetings, case conferences, performance improvement meetings, or Professional Advisory Committee meetings.",
        ],
      },
    ],
  },
  {
    pageNo: 35,
    title: "V. Guideline for Appropriate Referrals, including Timeliness (2-005.1)",
    sections: [
      {
        heading: "Policy",
        type: "list",
        items: [
          "Reports/reviews/investigations of suspected abuse/neglect are held in strict confidence.",
          "Organization maintains list of private/public community agencies for assessment of suspected/alleged abuse/neglect victims and clinician refers when necessary.",
          "Patients accepted for care without discrimination based on race, color, religion, age, gender, sexual orientation, disability (mental/physical), communicable disease, or place of national origin.",
          "Acceptance based on adequacy/suitability of resources to provide required services and reasonable expectation that medical, nursing, rehabilitative, and social needs can be adequately met in place of residence.",
          "Ability to pay (state/federal assistance, private insurance, personal assets) is another factor considered.",
          "Organization reserves right not to accept patient who does not meet admission criteria and refers patient to other resources if needs cannot be met.",
          "Once admitted, organization responsible for providing care/services within financial and service capabilities, mission, and law/regulation.",
          "Admission criteria: patient must be under care of physician/authorized practitioner who orders/approves service; skilled service required; patient must desire home health services; patients accepted regardless of payment source where appropriate for home health.",
        ],
      },
    ],
  },
  {
    pageNo: 36,
    title: "X, Y and Z. Patient Rights and Responsibilities (1-001.1) Start",
    sections: [
      {
        heading: "Cross-References",
        type: "list",
        items: [
          "Written information presented/reviewed based on patient and family/caregiver knowledge, skills, and identified needs.",
          "Case Manager documents patient/family caregiver education information in clinical notes; noncompliance/lack of understanding and instruction plan documented as part of plan of care.",
          "X. Information Regarding Services provided by other Members of Agency's personnel - Refer to Agency's Administrative Policies and Procedures.",
          "Y. Death and Dying - Refer to Agency's Clinical Policies and Procedures.",
        ],
      },
      {
        heading: "Z. Patient Rights and Responsibilities (1-001.1)",
        paragraphs: ["POLICY", "PROCEDURE"],
        type: "list",
        items: [
          "Each patient will be an active, informed participant in plan of care and may designate representative to act on his/her behalf.",
          "Policies regarding rights/responsibilities are available to personnel, patient, representatives, organizations, and interested public.",
          "Upon admission/as needed, patient informed of ownership/control of organization, mission and care/services arrangements, goals of care and interventions, staff identification, hours/how to obtain after-hours care, right to be informed and participate/refuse care, and right to formulate Advance Directives including policy on withholding resuscitative services/withdrawal of life-sustaining treatment.",
        ],
      },
    ],
  },
  {
    pageNo: 37,
    title: "Z. Patient Rights and Responsibilities - Procedure Continuation",
    sections: [
      {
        heading: "Patient/Family Responsibilities",
        type: "list",
        items: [
          "Provide accurate and complete information about present complaints, past illnesses, hospitalizations, medications, and other health-related matters.",
          "Report unexpected changes in patient's condition.",
          "Provide feedback regarding services, needs, and expectations.",
          "Ask questions regarding care/services.",
          "Follow instructions.",
          "Understand and accept consequences if care/services/treatment plan are not followed.",
          "Follow organization policies/procedures concerning patient care and conduct.",
          "Show respect and consideration for organization personnel and property.",
          "Meet financial commitments by promptly meeting agreed financial obligations.",
          "Admitting clinician provides written copy of Patient Rights and Responsibilities on admission.",
          "Rights/Responsibilities explained and distributed prior to initiation of services in language patient can reasonably understand.",
          "Patient requested to sign form; original kept in clinical record, copy maintained by patient; refusal documented including reason.",
          "Admitting clinician documents that patient received copy.",
          "If patient unable to understand rights/responsibilities, documentation is made in clinical note.",
          "If communication barrier exists, special devices or interpreters will be made available if possible.",
          "Written information provided in predominant languages of population served.",
          "When representative signs form, relationship explanation must be documented and kept in clinical record.",
        ],
      },
    ],
  },
  {
    pageNo: 38,
    title: "W. Emergency Preparedness within Agency and Home Care Setting (2-054)",
    sections: [
      {
        heading: "Admission Criteria Continuation and W. Policy/Procedure",
        type: "list",
        items: [
          "Patient must reside within organization's geographical service area.",
          "Physical facilities/equipment in patient's home must be adequate for safe/effective care.",
          "Services may be provided to Medicare-insured patients with primary need for skilled nursing/physical/speech therapy on intermittent basis who are homebound (restricted ability to leave residence except with aid/support or medically contraindicated conditions).",
          "Acceptance for home health services based on patient's willingness/ability to function in noninstitutional environment and willingness/ability/availability of family caregiver/significant individuals to participate in care.",
          "W Policy: patients and families/caregivers receive information on emergency management plan during initial visit.",
          "Upon admission, patient/family caregiver assessed regarding emergency management plan for home and special needs noted.",
          "Ongoing assessment of understanding; instruction per applicable policies including Emergency Management Plan.",
          "Assessment/instruction includes emergency preparedness/natural disaster plan components: emergency phone access (ambulance/police/fire/gas/electric/water), emergency supplies (food/water/heat/light/day-to-day necessities/medications), disaster follow-up and battery-powered radio access, procedures for care disruption due to natural disaster, actions for floods/storms/earthquakes, actions in case of fire, and evacuation plans for home.",
        ],
      },
    ],
  },
  {
    pageNo: 39,
    title: "BB. Cultural Diversity and CC. Corporate Compliance Program (4-040)",
    sections: [
      {
        heading: "Ethics and Corporate Compliance",
        type: "list",
        items: [
          "Organization personnel/patients/representatives/attending physicians may request in advance attendance at selected committee meeting when discussion relevant to patient care.",
          "Minutes maintained for meetings; for confidentiality, discussions involving individual patient cases or personnel will use identification numbers instead of names; minutes kept in Executive Director/Administrator office.",
          "Any organization personnel, physicians, or professionals involved in care may initiate referral for ethics consultation by notifying Clinical Director or Executive Director/Administrator.",
          "Issues involving conflicts/ethical concerns tracked and reported through performance improvement activities.",
          "BB. Cultural Diversity and Sensitivity - Refer to Agency's Administrative Policies and Procedures.",
          "CC. Corporate Compliance Program (4-040) Policy: organization established plan to ensure quality patient care fully complies with applicable state/federal laws/regulations.",
          "Policy includes employee education/training on applicable laws, periodic auditing/monitoring/oversight, environment encouraging reporting of non-compliance without fear of retribution, no delegation of compliance responsibility to persons prone to noncompliant behavior, and mechanisms to investigate/discipline/correct non-compliance.",
          "Plan includes Corporate Compliance Officer (CCO) with ultimate responsibility/accountability for compliance; however each employee remains responsible/accountable for own compliance. Confirmed non-compliance may be disciplined, including termination.",
        ],
      },
    ],
  },
];

const handbookDisplayOrder = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
  // G -> T ordered flow (data pages are mixed in source, so keep explicit sequence).
  11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 24, 29, 22, 23, 30, 21, 25, 26,
  27, 28,
  // U -> CC ordered flow.
  32, 33, 35, 38, 36, 31, 37, 34, 39,
];

const getOrderedHandbookPages = (pages) => {
  const byPageNo = new Map(pages.map((page) => [page.pageNo, page]));
  return handbookDisplayOrder
    .map((pageNo) => byPageNo.get(pageNo))
    .filter(Boolean);
};

const replaceCompanyReferences = (text, companyLabel) => {
  if (!text) return text;
  return text
    .replace(/INFINITE HOME HEALTH, INC'?s/gi, `${companyLabel}'s`)
    .replace(/INFINITE HOME HEALTH, INC/gi, companyLabel)
    .replace(/Refer to Agency's/gi, `Refer to ${companyLabel}'s`);
};

const renderTextWithBoldCompanyLabel = (text, companyLabel) => {
  const renderedText = replaceCompanyReferences(text, companyLabel);
  if (!renderedText || !companyLabel) return renderedText;

  const escapedCompanyLabel = companyLabel.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const companyLabelRegex = new RegExp(`(${escapedCompanyLabel})`, "gi");
  const parts = renderedText.split(companyLabelRegex);

  return parts.map((part, index) =>
    part.toLowerCase() === companyLabel.toLowerCase() ? (
      <strong key={`${part}-${index}`}>{part}</strong>
    ) : (
      part
    ),
  );
};

const Paragraph = ({ text, companyLabel }) => {
  const shouldKeepNormalWeight =
    text === "INFINITE HOME HEALTH, INC recognizes these values and their role in fulfilling our mission:";
  const isHeadingLike =
    text === "Mission" ||
    text === "Values" ||
    text === "POLICY" ||
    text === "Definitions" ||
    text === "PROCEDURE" ||
    text === "General Precautions" ||
    text === "Hand Hygiene" ||
    text === "PURPOSE" ||
    (!shouldKeepNormalWeight && /:$/.test(text)) ||
    (!/[.]/.test(text) && text.length <= 70 && text.trim().split(/\s+/).length <= 8);
  const renderedText = renderTextWithBoldCompanyLabel(text, companyLabel);
  if (isHeadingLike) {
    return <p className="font-semibold text-gray-900">{renderedText}</p>;
  }
  return <p>{renderedText}</p>;
};

const SectionRenderer = ({ section, companyLabel }) => (
  <section className="space-y-3">
    {section.heading && section.heading !== "Contents" ? (
      <h3 className="text-base font-semibold text-gray-900">
        {renderTextWithBoldCompanyLabel(section.heading, companyLabel)}
      </h3>
    ) : null}

    {section.paragraphs?.map((paragraph) => (
      <Paragraph key={paragraph} text={paragraph} companyLabel={companyLabel} />
    ))}

    {section.subheading ? (
      <h4 className="font-medium text-gray-900">
        {renderTextWithBoldCompanyLabel(section.subheading, companyLabel)}
      </h4>
    ) : null}

    {section.subItems?.length ? (
      <ol className="list-decimal pl-6 space-y-1">
        {section.subItems.map((item) => (
          <li key={item}>{renderTextWithBoldCompanyLabel(item, companyLabel)}</li>
        ))}
      </ol>
    ) : null}

    {section.type === "list" && section.items?.length && section.heading === "Contents" ? (
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="w-24 px-4 py-2 text-left font-semibold">Sr. No.</th>
              <th className="px-4 py-2 text-left font-semibold">Title</th>
            </tr>
          </thead>
          <tbody>
            {section.items.map((item, index) => (
              <tr key={item} className="border-t border-gray-200 odd:bg-white even:bg-gray-50">
                <td className="px-4 py-2 font-medium text-gray-700">{index + 1}</td>
                <td className="px-4 py-2">{renderTextWithBoldCompanyLabel(item, companyLabel)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : null}

    {section.type === "list" && section.items?.length && section.heading !== "Contents" ? (
      <ul className="list-disc pl-6 space-y-1">
        {section.items.map((item) => (
          <li key={item}>{renderTextWithBoldCompanyLabel(item, companyLabel)}</li>
        ))}
      </ul>
    ) : null}

    {section.table?.headers?.length ? (
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              {section.table.headers.map((header) => (
                <th key={header} className="px-4 py-2 text-left font-semibold align-top">
                  {renderTextWithBoldCompanyLabel(header, companyLabel)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-gray-200">
              {section.table.columns?.map((column, colIndex) => (
                <td key={`${section.heading || "table"}-${colIndex}`} className="px-4 py-2 align-top">
                  <ul className="space-y-1">
                    {column.map((entry) => (
                      <li key={entry}>{renderTextWithBoldCompanyLabel(entry, companyLabel)}</li>
                    ))}
                  </ul>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    ) : null}
  </section>
);

const EmployeeHandbookReadOnly = ({ onClose }) => {
  const { officeLogo, companyName, tenant } = useCurrentUser();
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const orderedPages = getOrderedHandbookPages(handbookPages);
  const totalPages = orderedPages.length;
  const currentPage = orderedPages[currentPageIndex];
  const companyLabel = companyName || "Company";

  const getLogoUrl = () => {
    const tenantLogo = officeLogo || tenant?.officeLogo || "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";

    if (!tenantLogo || !String(tenantLogo).trim()) {
      return `${origin}${DEFAULT_LOGO}`;
    }
    if (tenantLogo.startsWith("http")) return tenantLogo;

    const normalizedPath = tenantLogo.startsWith("/") ? tenantLogo : `/${tenantLogo}`;
    return `${origin}${normalizedPath}`;
  };

  return (
    <>
      <CloseButton onClose={onClose} />
      <div className="max-w-5xl mx-auto bg-white shadow-xl rounded-2xl my-8">
        <div className="p-6 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <img
              src={getLogoUrl()}
              alt="Company Logo"
              className="h-16 w-auto max-w-[180px] object-contain"
              onError={(e) => {
                const origin = typeof window !== "undefined" ? window.location.origin : "";
                e.target.src = `${origin}${DEFAULT_LOGO}`;
              }}
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Employee Handbook</h1>
              <p className="text-sm text-gray-600 font-semibold">{companyLabel}</p>
            </div>
          </div>
        </div>

        <div className="p-8 text-sm leading-relaxed text-gray-800">
          <article className="border border-gray-200 rounded-xl p-6 bg-gray-50/40 min-h-[68vh]">
            <div className="mb-4 pb-3 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{currentPage.title}</h2>
            </div>
            <div className="space-y-6">
              {currentPage.sections.map((section) => (
                <SectionRenderer
                  key={`${currentPage.pageNo}-${section.heading || "section"}`}
                  section={section}
                  companyLabel={companyLabel}
                />
              ))}
            </div>
          </article>

          <div className="mt-6 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setCurrentPageIndex((prev) => Math.max(prev - 1, 0))}
              disabled={currentPageIndex === 0}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 bg-gray-50 hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="inline-flex rotate-180" aria-hidden="true">
                <ArrowRightIcon size={14} />
              </span>
              Previous
            </button>
            <span className="inline-flex items-center justify-center min-w-[78px] px-4 py-2 rounded-full border border-gray-200 bg-gray-50 text-sm font-semibold text-gray-700">
              {currentPageIndex + 1} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPageIndex((prev) => Math.min(prev + 1, totalPages - 1))}
              disabled={currentPageIndex === totalPages - 1}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Next
              <span className="inline-flex" aria-hidden="true">
                <ArrowRightIcon size={14} />
              </span>
            </button>
          </div>

          <section className="mt-3 border border-gray-200 bg-gray-50 rounded-b-xl px-6 py-6 text-center">
            <span className="text-2sm sm:text-base font-semibold text-gray-700">{companyLabel}</span>
          </section>
        </div>
      </div>
    </>
  );
};

export default EmployeeHandbookReadOnly;
